<template>
	<div v-if="isAvailable" class="relative w-full min-h-[40px]">
		<div v-if="!isButtonVisible" class="absolute inset-0">
			<div class="w-full h-full border border-ihh-gray/40 rounded flex items-center justify-center bg-white">
				<Spinner :small="true" class="w-4 h-4" color="border-t-ihh-salmon" />
			</div>
		</div>
		<button
			class="h-10 px-4 items-center w-full bg-black hover:opacity-90 text-white rounded font-medium text-base transition duration-200"
			:class="{ invisible: !isButtonVisible, 'opacity-70 cursor-not-allowed': isProcessing }"
			@click="processApplePay"
			:disabled="isProcessing"
		>
			<div class="flex w-full justify-center items-center gap-2">
				<svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
					<path
						d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"
					/>
				</svg>
				<span>Pay</span>
			</div>
		</button>
	</div>
</template>

<script>
import ApplePayHelpers from '../../helpers/apple-pay.js'
import Braintree from '../../helpers/braintree.js'
import helpers from '../../helpers/checkout.js'
import axios from 'axios'
import { useCheckoutStore } from '../../stores/store.js'
import Swal from 'sweetalert2'
import { CheckoutAction, logCheckoutPaymentFailure } from '../../helpers/sentry.js'

import ShippingAndBilling from './shipping-and-billing.vue'
import ExpressCheckoutSummary from './express-checkout-summary.vue'

export default {
	name: 'ApplePayButton',
	components: {
		ShippingAndBilling,
		ExpressCheckoutSummary,
	},
	inject: ['store'],
	emits: ['updateAvailability'],
	data() {
		return {
			checkout: useCheckoutStore(),
			isAvailable: false,
			isButtonVisible: false,
			applePayInstance: null,
			showModal: false,
			deviceData: null,
			isProcessing: false, // Flag to prevent duplicate transactions
			// Store the complete addresses for later use
			_completeShippingAddress: null,
			_completeBillingAddress: null,
		}
	},

	async mounted() {
		await this.checkAvailability()
	},
	methods: {
		abortApplePaySession(session) {
			if (!session) return
			try {
				session.abort()
			} catch (err) {
				// Session may already be closed (InvalidAccessError) — ignore
				console.warn('Apple Pay session.abort skipped:', err?.message || err)
			}
		},
		async checkAvailability() {
			try {
				const applePayAvailable = ApplePayHelpers.isAvailable()
				if (applePayAvailable) {
					console.log('Apple Pay is available')
					this.isAvailable = true
					this.$emit('updateAvailability', true)

					await this.applePaySetup()
				} else {
					console.log('Apple Pay is not available')
					this.$emit('updateAvailability', false)
					this.isButtonVisible = false
					this.isAvailable = false
				}
			} catch (error) {
				console.log('Error checking Apple Pay availability:', error)
				this.$emit('updateAvailability', false)
				this.isButtonVisible = false
				this.isAvailable = false
			}
		},
		async applePaySetup() {
			console.log('Configuring Apple Pay instance')

			// Fetch Braintree client token if not already available
			if (!this.clientToken) {
				console.log('fetching client token for Apple Pay setup...')
				const fetchClientToken = await Braintree.getClientToken(this.store.api)
				if (!this.clientToken) this.clientToken = fetchClientToken?.token
			}

			// Create the client and apple pay instances
			const res = await ApplePayHelpers.setup(this.clientToken)
			this.applePayInstance = res.applePayInstance
			this.deviceData = await Braintree.dataCollector(res.clientInstance)
			console.log('Apple Pay instance: ', this.applePayInstance)

			this.isButtonVisible = true
		},
		async processApplePay() {
			// Prevent multiple clicks from initiating multiple payment sessions
			if (this.isProcessing) {
				console.log('Payment processing already in progress, ignoring click')
				return
			}

			this.isProcessing = true
			console.log('Apple Pay button clicked...')

			if (!(await helpers.requireExpressCheckoutValidated())) {
				this.isProcessing = false
				return
			}

			// Create payment request
			const applePayInstance = this.applePayInstance

			// Ensure we have a valid amount
			const totalAmount = parseFloat(this.checkout.remainingBalance)
			console.log('Total amount for Apple Pay:', totalAmount)

			if (isNaN(totalAmount) || totalAmount <= 0) {
				console.error('Invalid total amount for Apple Pay:', this.checkout.remainingBalance)
				return Swal.fire({
					icon: 'error',
					title: 'Error',
					text: 'Invalid order amount. Please try again.',
					confirmButtonText: 'OK',
				})
			}

			const paymentRequest = applePayInstance.createPaymentRequest({
				total: {
					label: 'In His Hands Birth Supply',
					amount: totalAmount.toFixed(2),
				},
				requiredBillingContactFields: ['postalAddress', 'name', 'email', 'phone'],

				// Enable below shipping options to edit shipping within the Apple Pay Sheet
				// requiredShippingContactFields: ['postalAddress', 'name', 'phone', 'email'],
				// shippingMethods: [],
			})

			// Create the Apple Pay session
			let session = null
			try {
				console.log('Creating new session...')
				session = new ApplePaySession(3, paymentRequest)
				console.log('Session object: ', session)
				console.log('Session stringified: ', JSON.stringify(session))

				// Add cancel handler to reset processing state when user cancels
				session.oncancel = () => {
					console.log('Apple Pay session was cancelled by the user')
					this.isProcessing = false
				}
			} catch (error) {
				console.error('Error creating Apple Pay session:', error)
				this.isProcessing = false // Reset processing flag on error
				return null
			}

			// Set up session callbacks
			session.onvalidatemerchant = (event) => {
				applePayInstance
					.performValidation({
						validationURL: event.validationURL,
						displayName: 'In His Hands Birth Supply',
					})
					.then(function (merchantSession) {
						session.completeMerchantValidation(merchantSession)
						console.log('Merchant session validated.')
					})
					.catch((validationErr) => {
						// You should show an error to the user, e.g. 'Apple Pay failed to load.'
						console.error('Error validating merchant:', validationErr)
						this.abortApplePaySession(session)
						this.isProcessing = false // Reset processing flag on validation error
					})
			}
			// session.onshippingcontactselected = async (event) => {
			// 	try {
			// 		console.log('Shipping contact selected:', event.shippingContact)

			// 		// Extract partial address information (similar to PayPal's onShippingChange)
			// 		const stateName = State.getStateByCodeAndCountry(event.shippingContact.administrativeArea, event.shippingContact.countryCode)?.name

			// 		// Update only the partial address first (like PayPal does)
			// 		this.checkout.shipping.shippingAddress = {
			// 			city: event.shippingContact.locality,
			// 			stateOrProvince: stateName,
			// 			countryCode: event.shippingContact.countryCode,
			// 			postalCode: event.shippingContact.postalCode,
			// 		}

			// 		// Get shipping estimates with the partial address (like PayPal does)
			// 		await helpers.getShippingEstimates()

			// 		// Wait for Vue to update the DOM and state
			// 		await this.$nextTick()

			// 		// Map shipping options to Apple Pay format
			// 		const shippingMethods = this.checkout.shipping.shippingOptions.map((option) => ({
			// 			identifier: option.id,
			// 			label: helpers.formatShippingDescription(option.description, true),
			// 			detail: option.description,
			// 			amount: option.cost,
			// 		}))

			// 		// Complete the shipping contact selection
			// 		session.completeShippingContactSelection({
			// 			status: ApplePaySession.STATUS_SUCCESS,
			// 			newShippingMethods: shippingMethods,
			// 			newTotal: {
			// 				label: 'In His Hands Birth Supply',
			// 				amount: this.checkout.remainingBalance,
			// 			},
			// 			newLineItems: [
			// 				{ label: 'Subtotal', amount: (this.store?.cart?.total - this.checkout.giftCertificateAmount).toFixed(2) },
			// 				{ label: 'Shipping', amount: (this.checkout.shippingTotal - this.checkout.shippingDiscount).toFixed(2) },
			// 			],
			// 		})
			// 	} catch (error) {
			// 		console.error('Error updating shipping contact:', error)
			// 		session.abort()
			// 	}
			// }
			// session.onshippingmethodselected = async (event) => {
			// 	try {
			// 		console.log('on shipping method selected:', event.shippingMethod)

			// 		const selectedShippingMethod = event.shippingMethod

			// 		this.checkout.selectShipping({
			// 			id: selectedShippingMethod.identifier,
			// 			cost: selectedShippingMethod.amount,
			// 			description: selectedShippingMethod.detail,
			// 		})

			// 		// Use $nextTick to ensure Vue has updated the DOM and the state before proceeding
			// 		await this.$nextTick()

			// 		const newTotal = {
			// 			label: 'In His Hands Birth Supply',
			// 			amount: this.checkout.remainingBalance,
			// 		}

			// 		// Update line items
			// 		const newLineItems = [
			// 			{ label: 'Subtotal', amount: (this.store?.cart?.total - this.checkout.giftCertificateAmount).toFixed(2) },
			// 			{ label: 'Shipping', amount: (this.checkout.shippingTotal - this.checkout.shippingDiscount).toFixed(2) },
			// 		]

			// 		console.log('newTotal:', newTotal)
			// 		console.log('newLineItems:', newLineItems)
			// 		console.log('completing Apple Pay shipping method selection...')

			// 		// Ensure we have valid data before completing
			// 		if (!newTotal || typeof newTotal.amount === 'undefined') {
			// 			console.error('Invalid newTotal object', newTotal)
			// 			throw new Error('Invalid total amount')
			// 		}

			// 		// Respond to Apple Pay with the updated total and line items
			// 		session.completeShippingMethodSelection({
			// 			status: 'STATUS_SUCCESS',
			// 			newTotal: newTotal,
			// 			newLineItems: newLineItems,
			// 		})

			// 		console.log('Shipping method selection completed successfully')
			// 	} catch (error) {
			// 		console.error('Error updating shipping method:', error)
			// 		session.abort()
			// 	}
			// }
			session.onpaymentauthorized = (event) => {
				try {
					console.log('Payment authorized by user')

					// Use $nextTick to ensure Vue has updated the DOM and state before proceeding
					// this.$nextTick(() => {
					// 	console.log('after next tick')

					// })
					applePayInstance
						.tokenize({
							token: event.payment.token,
						})
						.then(async (payload) => {
							// Send payload.nonce to your server.
							console.log('Event body:', event)

							// Format the complete shipping and billing contacts
							// const billingContact = this.formatAppleContact(event.payment.billingContact)
							// const shippingContact = this.formatAppleContact(event.payment.shippingContact)

							// Store the complete addresses for later use in completeOrder
							// But don't update the checkout store yet to avoid triggering BigCommerce validation
							// this._completeShippingAddress = shippingContact
							// this._completeBillingAddress = { ...billingContact, email: shippingContact.email }

							const nonce = payload.nonce
							const billingEmail = event.payment.billingContact.emailAddress

							return await this.completeOrder(nonce, session, billingEmail)
						})
						.catch((tokenizeErr) => {
							console.error('Error tokenizing Apple Pay:', tokenizeErr)
							logCheckoutPaymentFailure({
								message: 'Apple Pay tokenization failed',
								paymentMethod: 'apple_pay',
								action: CheckoutAction.TOKENIZE_PAYMENT,
								error: tokenizeErr,
								store: this.store,
								checkout: this.checkout,
								extra: { phase: 'tokenize' },
							})
							session.completePayment(ApplePaySession.STATUS_FAILURE)
							this.isProcessing = false // Reset processing flag on tokenization error
						})
				} catch (error) {
					console.error('Payment processing failed:', error)
					session.completePayment(ApplePaySession.STATUS_FAILURE)
					this.abortApplePaySession(session)
					this.isProcessing = false // Reset processing flag on payment processing error
				}
			}

			// Start the Apple Pay session
			session.begin()
		},
		formatAppleContact(contact) {
			// Ensure the shippingContact object is provided
			if (!contact) {
				console.error('Contact is undefined or null.')
				return
			}

			// Map the values to your shipping address object
			return {
				firstName: contact.givenName || '', // First name
				lastName: contact.familyName || '', // Last name
				email: contact?.emailAddress || '',
				address1: contact.addressLines?.[0] || '', // First line of the address
				address2: contact.addressLines?.[1] || '', // Second line of the address, if any
				city: contact.locality || '', // City
				stateOrProvince: contact.administrativeArea || '', // State/Province
				countryCode: contact.countryCode || '', // ISO country code
				postalCode: contact.postalCode || '', // ZIP/Postal code
			}
		},

		async completeOrder(nonce, session) {
			try {
				const processPayment = await axios.post(`${this.store.api}/controllers/checkout/applePay`, {
					orderComments: this.checkout.orderDetails.comments,
					checkoutId: this.store.cart.bcId,
					billingAddress: this.checkout.billing.billingAddress,
					existingBcOrderId: this.checkout.bcOrderId,
					staffNote: this.store.cart?.staffNote || '',
					useStoreCredit: this.checkout.payment.useStoreCredit,
					certificate: this.store.cart?.certificate?.balance ? this.store.cart.certificate : null,
					nonce: nonce,
					amount: this.grandTotal,
					deviceData: this.deviceData,
					email: this.store?.user?.email || this.checkout.guest.email,
					kitId: this.store.cart?.kitId || null,
				})

				console.log('processPayment: ', processPayment)

				if (!processPayment?.data?.success) {
					session.completePayment(ApplePaySession.STATUS_FAILURE)
					this.isProcessing = false // Reset processing flag on error
					if (processPayment?.data?.bcOrderId) {
						this.checkout.bcOrderId = processPayment.data.bcOrderId
					}
					if (await helpers.handlePlaceOrderStockFailure(processPayment?.data)) {
						return
					}
					logCheckoutPaymentFailure({
						message: 'Apple Pay checkout failed',
						paymentMethod: 'apple_pay',
						action: CheckoutAction.PLACE_ORDER,
						response: processPayment,
						store: this.store,
						checkout: this.checkout,
						bcOrderId: processPayment?.data?.bcOrderId,
					})
					Swal.fire({
						icon: 'error',
						title: processPayment?.data?.code || 'Error',
						text: processPayment?.data?.message || 'There was an error completing your order. Please try again or contact customer support.',
						confirmButtonText: 'OK',
					})
					return
				}

				const redirectUrl = await helpers.completeCheckoutSuccess(processPayment)

				// Update the Apple Pay session
				session.completePayment(ApplePaySession.STATUS_SUCCESS)

				// Wait a moment before proceeding to allow Apple Pay sheet to dismiss properly
				setTimeout(() => {
					this.$router.push(redirectUrl)
				}, 500)

				return
			} catch (error) {
				console.error('Error completing Apple Pay checkout:', error)
				session.completePayment(ApplePaySession.STATUS_FAILURE)
				if (error?.response?.data?.bcOrderId) {
					this.checkout.bcOrderId = error.response.data.bcOrderId
				}
				logCheckoutPaymentFailure({
					message: 'Apple Pay checkout exception',
					paymentMethod: 'apple_pay',
					action: CheckoutAction.PLACE_ORDER,
					error,
					store: this.store,
					checkout: this.checkout,
					bcOrderId: error?.response?.data?.bcOrderId,
				})
				this.isProcessing = false // Reset processing flag on error
				Swal.fire({
					icon: 'error',
					title: 'Error Processing Checkout',
					text: error?.response?.data?.code
						? `${error?.response?.data?.code}: ${error?.response?.data?.message}`
						: error?.message || 'There was an error completing your order. Please try again or contact customer support.',
					confirmButtonText: 'OK',
				})
			}
		},
	},

	computed: {
		subTotal() {
			return Number(parseFloat(this.store?.cart?.total).toFixed(2))
		},
		subTotalWithDiscounts() {
			// coupon discounts are already applied to the cart subTotal
			const total = this.subTotal - this.checkout.giftCertificateAmount - this.checkout.storeCredit
			return Number(parseFloat(total).toFixed(2))
		},
		grandTotal() {
			let total = this.subTotalWithDiscounts + (this.checkout.shipping.selectedShipping?.cost || 0)
			if (total <= 0) total = this.subTotal
			return Number(parseFloat(total).toFixed(2))
		},
	},
}
</script>

<style scoped></style>
